import {
  AdditiveBlending,
  CylinderGeometry,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshBasicMaterial,
  Vector3,
} from 'three';
import type { Scene } from 'three';

/**
 * The minimum a pooled bolt needs: where it is, which way it travels, and an
 * optional size multiplier. A simulated `Projectile` satisfies this, and so
 * does the screen-anchored clone the bullet-time overlay builds for a bolt
 * that has left the viewport, so one pool draws both.
 */
export interface BoltVisual {
  position: Vector3;
  velocity: Vector3;
  /** Uniform scale for the drawn bolt; 1 (or omitted) is the world size. */
  scale?: number;
}

const FORWARD = new Vector3(0, 0, -1);
const scratchDirection = new Vector3();
const POOL_SIZE = 24;

interface BoltMesh {
  group: Group;
  core: Mesh;
  trail: Mesh;
}

/**
 * Pooled visual layer for the drones' energy bolts.
 *
 * The simulation only stores a position and velocity. This view draws a bright
 * core with a short trail so the bolt reads as a moving object rather than an
 * instant health-bar change. The trail points backwards along the velocity.
 */
export class BoltView {
  readonly group = new Group();
  private readonly bolts: BoltMesh[] = [];
  private active = 0;

  constructor(scene: Scene) {
    const coreGeometry = new IcosahedronGeometry(0.09, 0);
    const trailGeometry = new CylinderGeometry(0.018, 0.055, 1.1, 6);
    const coreMaterial = new MeshBasicMaterial({
      color: 0xff8a5a,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
    });
    const trailMaterial = new MeshBasicMaterial({
      color: 0xff3a2a,
      transparent: true,
      opacity: 0.55,
      blending: AdditiveBlending,
      depthWrite: false,
    });

    for (let index = 0; index < POOL_SIZE; index += 1) {
      const core = new Mesh(coreGeometry, coreMaterial);
      const trail = new Mesh(trailGeometry, trailMaterial);
      // CylinderGeometry runs along local +Y; rotate it onto local +Z, which is
      // behind the core because the group's forward is -Z.
      trail.rotation.x = Math.PI / 2;
      trail.position.z = 0.55;

      const group = new Group();
      group.add(core, trail);
      group.visible = false;
      this.group.add(group);
      this.bolts.push({ group, core, trail });
    }

    scene.add(this.group);
  }

  /** Show one pooled bolt per visual and orient it along its path. */
  sync(visuals: readonly BoltVisual[]): void {
    this.active = Math.min(visuals.length, this.bolts.length);
    for (let index = 0; index < this.bolts.length; index += 1) {
      const entry = this.bolts[index];
      const visual = visuals[index];
      if (!visual) {
        entry.group.visible = false;
        continue;
      }

      entry.group.visible = true;
      entry.group.position.copy(visual.position);
      entry.group.scale.setScalar(visual.scale ?? 1);
      scratchDirection.copy(visual.velocity);
      if (scratchDirection.lengthSq() > 1e-8) {
        scratchDirection.normalize();
        entry.group.quaternion.setFromUnitVectors(FORWARD, scratchDirection);
      }
    }
  }

  /** How many bolts are currently drawn. */
  get activeCount(): number {
    return this.active;
  }

  dispose(): void {
    const first = this.bolts[0];
    if (!first) return;
    first.core.geometry.dispose();
    first.trail.geometry.dispose();
    (first.core.material as MeshBasicMaterial).dispose();
    (first.trail.material as MeshBasicMaterial).dispose();
    this.group.removeFromParent();
    this.bolts.length = 0;
    this.active = 0;
  }
}
